import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { motion } from 'framer-motion';
import { Flame, Trophy, Star, Target, Loader2 } from 'lucide-react';

const StreakWidget = ({ childId, compact = false }) => {
  const { api } = useAuth();
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (childId) {
      fetchStreak();
    }
  }, [childId]);

  const fetchStreak = async () => {
    try {
      const response = await api.get(`/children/${childId}/streak`);
      setStreak(response.data);
    } catch (error) {
      console.error('Failed to fetch streak:', error);
      setStreak({
        current_streak: 0,
        longest_streak: 0,
        total_bonus_xp: 0,
        next_milestone: { days: 3, days_remaining: 3, name: "3-Day Streak", bonus_xp: 10, emoji: "🔥" }
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
        </CardContent>
      </Card>
    );
  }

  const currentStreak = streak?.current_streak || 0;
  const nextMilestone = streak?.next_milestone;

  if (compact) {
    return (
      <Card className={`${currentStreak > 0 ? 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200' : 'bg-slate-50'}`}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <motion.div
                animate={currentStreak > 0 ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Flame className={`w-6 h-6 ${currentStreak > 0 ? 'text-orange-500' : 'text-slate-300'}`} />
              </motion.div>
              <div>
                <p className="font-bold text-lg text-slate-800">{currentStreak} day{currentStreak !== 1 ? 's' : ''}</p>
                <p className="text-xs text-slate-500">Current Streak</p>
              </div>
            </div>
            {nextMilestone && currentStreak > 0 && (
              <div className="text-right">
                <p className="text-xs text-orange-600">{nextMilestone.days_remaining} to go</p>
                <p className="text-xs text-slate-500">+{nextMilestone.bonus_xp} XP</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden" data-testid="streak-widget">
      {/* Header with fire animation */}
      <div className={`p-4 ${currentStreak > 0 ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-slate-200'}`}>
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <motion.div
              animate={currentStreak > 0 ? { 
                scale: [1, 1.2, 1],
                rotate: [0, 5, -5, 0]
              } : {}}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <Flame className={`w-10 h-10 ${currentStreak > 0 ? '' : 'text-slate-400'}`} />
            </motion.div>
            <div>
              <h3 className={`font-bold text-2xl ${currentStreak > 0 ? '' : 'text-slate-600'}`}>
                {currentStreak} Day Streak
              </h3>
              <p className={`text-sm ${currentStreak > 0 ? 'text-orange-100' : 'text-slate-500'}`}>
                {currentStreak > 0 ? "Keep it going!" : "Complete all daily tasks to start!"}
              </p>
            </div>
          </div>
          {currentStreak > 0 && (
            <div className="text-right">
              <p className="text-sm text-orange-100">Longest</p>
              <p className="font-bold text-xl">{streak?.longest_streak || 0}</p>
            </div>
          )}
        </div>
      </div>

      <CardContent className="p-4">
        {/* Progress to next milestone */}
        {nextMilestone && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">
                Next: {nextMilestone.emoji} {nextMilestone.name}
              </span>
              <span className="text-sm text-orange-600 font-medium">
                +{nextMilestone.bonus_xp} XP
              </span>
            </div>
            <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(((nextMilestone.days - nextMilestone.days_remaining) / nextMilestone.days) * 100, 100)}%` }}
                transition={{ duration: 0.5 }}
                className="h-full bg-gradient-to-r from-orange-400 to-red-500 rounded-full"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {nextMilestone.days_remaining} more day{nextMilestone.days_remaining !== 1 ? 's' : ''} to reach milestone
            </p>
          </div>
        )}

        {/* Milestone badges */}
        <div className="flex flex-wrap gap-2">
          {[
            { days: 3, emoji: "🔥", achieved: currentStreak >= 3 },
            { days: 7, emoji: "🔥🔥", achieved: currentStreak >= 7 },
            { days: 14, emoji: "🔥🔥🔥", achieved: currentStreak >= 14 },
            { days: 30, emoji: "🏆🔥", achieved: currentStreak >= 30 },
          ].map((milestone) => (
            <motion.div
              key={milestone.days}
              whileHover={{ scale: 1.05 }}
              className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 ${
                milestone.achieved
                  ? 'bg-orange-100 text-orange-700'
                  : 'bg-slate-100 text-slate-400'
              }`}
            >
              <span>{milestone.emoji}</span>
              <span>{milestone.days}d</span>
            </motion.div>
          ))}
        </div>

        {/* Total bonus XP */}
        {streak?.total_bonus_xp > 0 && (
          <div className="mt-4 p-3 bg-amber-50 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
              <span className="text-sm text-slate-700">Total Streak Bonus</span>
            </div>
            <span className="font-bold text-amber-600">+{streak.total_bonus_xp} XP</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StreakWidget;
